// src/services/authService.js

import {
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { auth, db } from "../config/firebase";
import { notificationService } from "./notificationService";
import { USER_ROLES } from "../config/constants";
import { errorHandler } from "../utils/errorHandler";

/**
 * Liste des rôles utilisateurs utilisés dans l'application.
 * Définie ici pour éviter une référence non définie à USER_ROLES.
 */

/**
 * Service d'authentification centralisé (Firebase Auth + Firestore user doc)
 */

export const authService = {
  /**
   * Connecte un utilisateur et met à jour lastLogin.
   * @param {string} email
   * @param {string} password
   * @param {boolean} rememberMe
   * @returns {Promise<import('firebase/auth').UserCredential>}
   */

  async login(email, password, rememberMe = false) {
    try {
      // Définir la persistance selon le choix de l'utilisateur
      const persistence = rememberMe
        ? browserLocalPersistence
        : browserSessionPersistence;
      await setPersistence(auth, persistence);

      const result = await signInWithEmailAndPassword(auth, email, password);

      // Mettre à jour la dernière connexion
      await updateDoc(
        doc(db, "USERS", result.user.uid),
        {
          lastLogin: serverTimestamp(),
        },
        { merge: true }
      );

      notificationService?.success?.("Connexion réussie");
      return result;
    } catch (error) {
      errorHandler?.logError?.(error, {
        context: "auth",
        action: "login",
        email,
      });
      notificationService?.handleFirebaseError?.(error);
      throw error;
    }
  },

  /**
   * Déconnecte l'utilisateur.
   * @returns {Promise<void>}
   */

  async logout() {
    try {
      await signOut(auth);
      notificationService?.info?.("Déconnexion réussie");
    } catch (error) {
      errorHandler?.logError?.(error, { context: "auth", action: "logout" });
      notificationService?.handleFirebaseError?.(error);
      throw error;
    }
  },

  /**
   * Enregistre un nouvel utilisateur et crée son document dans Firestore.
   * @param {string} email
   * @param {string} password
   * @param {string} firstName
   * @param {string} lastName
   * @param {string} [phoneNumber] - Numéro de téléphone optionnel
   * @param {string} role
   * @returns {Promise<import('firebase/auth').UserCredential>} L'objet User de Firebase Auth.
   */
  async register(
    email,
    password,
    firstName,
    lastName,
    phoneNumber = null,
    role = USER_ROLES.COMMERCIAL
  ) {
    try {
      // 1. Création de l'utilisateur dans Firebase Auth

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // 2. Création du document utilisateur dans Firestore
      await setDoc(
        doc(db, "USERS", user.uid),
        {
          uid: user.uid,
          email: user.email,
          firstName: firstName,
          lastName: lastName,
          role: role,
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
          phoneNumber: phoneNumber,
          company: null, // Optionnel
          avatar: null, // Optionnel
          preferences: {
            theme: "light",
            notifications: true,
            language: "fr",
          },
        },
        { merge: true }
      );

      notificationService?.success?.(
        `Compte créé pour ${firstName ?? ""} ${lastName}`
      );
      return user;
    } catch (error) {
      errorHandler.logError(error, {
        context: "auth",
        userAction: "register",
        email: email,
      });
      notificationService.handleFirebaseError(error);
      throw error;
    }
  },

  /**
   * Met à jour le profil utilisateur
   * @param {string} uid - ID de l'utilisateur
   * @param {Object} profileData - Données du profil à mettre à jour
   * @returns {Promise<void>}
   */
  async updateProfile(uid, profileData = {}) {
    try {
      await updateDoc(doc(db, "USERS", uid), {
        ...profileData,
        updatedAt: serverTimestamp(),
      });
      notificationService?.success?.("Profil mis à jour");
    } catch (error) {
      errorHandler?.logError?.(error, {
        context: "auth",
        action: "updateProfile",
        uid,
      });
      notificationService?.handleFirebaseError?.(error);
      throw error;
    }
  },

  /**
   * Récupère le profil utilisateur
   * @param {string} uid - ID de l'utilisateur
   * @returns {Promise<Object|null>} Données du profil
   */
  async getProfile(uid) {
    try {
      const userDoc = await getDoc(doc(db, "USERS", uid));
      return userDoc.exists() ? userDoc.data() : null;
    } catch (error) {
      errorHandler?.logError?.(error, {
        context: "auth",
        action: "getProfile",
        uid,
      });
      throw error;
    }
  },
};
